import { useState } from "react"

import { Module, useCreateShareMutation } from "../generated/graphql"
import { graphQLClient } from "../graphql/client"

// A share code (`?s=`): 8 lower-case letters and digits, made by the API's share log
// (api/src/utils/shares.ts).
export const SHARE_CODE = /^[a-z0-9]{8}$/

// How long a copy waits for a share code before it copies the plain link.
const SHARE_CODE_WAIT_MS = 2000

// Safari only lets a page write to the clipboard during the click, so the text goes in as a promise
// the clipboard waits for. Browsers without ClipboardItem get it once it's ready.
const copyText = async (text: Promise<string>) => {
  if (typeof ClipboardItem !== "undefined" && navigator.clipboard.write) {
    const blob = text.then((value) => new Blob([value], { type: "text/plain" }))
    await navigator.clipboard.write([new ClipboardItem({ "text/plain": blob })])
  } else {
    await navigator.clipboard.writeText(await text)
  }
}

// The coming-soon page's copy buttons: copies this page's link with a new share code from the API
// for a signed-in member, never the code or the older `ref` the visitor arrived with. Signed out,
// or when the API refuses, fails or is slow, the plain link is copied. `copied` is true for 2
// seconds after a copy.
export const useCopyShareLink = (communityCode: string, signedIn: boolean) => {
  const { mutateAsync: createShare } = useCreateShareMutation(graphQLClient)
  const [copied, setCopied] = useState(false)

  const shareLink = async () => {
    const url = new URL(window.location.href)
    url.searchParams.delete("s")
    url.searchParams.delete("ref")
    const code = signedIn
      ? await Promise.race([
          createShare({ communityCode, module: Module.Landing })
            .then((result) => result.createShare)
            .catch(() => null),
          new Promise<null>((resolve) => setTimeout(() => resolve(null), SHARE_CODE_WAIT_MS)),
        ])
      : null
    if (code) url.searchParams.set("s", code)
    return url.toString()
  }

  const copy = async () => {
    try {
      await copyText(shareLink())
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch {
      // Clipboard API unavailable (e.g. non-HTTPS)
    }
  }

  return { copied, copy }
}
